import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  TrendingDown, TrendingUp, Sparkles, GitBranch, Loader2,
  Calendar, ArrowRight, FileCode2, ChevronRight, Share2, Check,
  PartyPopper, Flag, Linkedin, Trophy, Twitter, Bell, Mail,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import PublicNavbar from '../components/PublicNavbar';
import FooterBackground from '../components/FooterBackground';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public momentum mirror at /build/momentum
 * -----------------------------------------
 * Read-only public version of the admin momentum overlay. Pulls from
 * /api/build/momentum (no auth, redacted — never leaks file paths,
 * timestamps, or per-module flags). Lets prospects/investors see weekly
 * progress at a glance.
 */
const BuildMomentumPage = () => {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [recent, setRecent] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, t, r, ss, ms] = await Promise.all([
          axios.get(`${API}/build/momentum`),
          axios.get(`${API}/build/timeline?days=30`),
          axios.get(`${API}/build/recent-extractions?limit=5`),
          axios.get(`${API}/build/share-stats-public?days=7`).catch(() => ({ data: null })),
          axios.get(`${API}/build/milestones`).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setData(m.data);
          setTimeline(t.data);
          setRecent(r.data);
          setShareStats(ss.data);
          setMilestones(ms.data);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Track UTM-tagged visits (fire-and-forget — never blocks page paint).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    if (!source) return;
    const snapshot = parseInt(params.get('v'), 10) || null;
    axios
      .post(`${API}/build/share-visit`, { source, snapshot })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative" data-testid="public-momentum-page">
      <PublicNavbar />
      <div className="relative">
        <FooterBackground />
        <div className="relative max-w-5xl mx-auto px-4 pt-16 pb-20">
          <div className="text-center mb-12">
            <Badge className="bg-emerald-500/15 text-cyan-300 border-emerald-500/30 text-[11px] font-bold tracking-widest mb-4">
              SHIPPING WEEKLY
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
              This week,{' '}
              <span className="bg-clip-text text-transparent bg-cyan-400 bg-gradient-to-r from-cyan-400 to-emerald-400">
                we shipped.
              </span>
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              A live, redacted view of our weekly engineering velocity.
              No marketing fluff — just the numbers.
            </p>
          </div>

          {loading && (
            <div className="flex justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-cyan-400" />
            </div>
          )}

          {error && !loading && (
            <Card className="bg-rose-500/10 border-rose-500/30 max-w-lg mx-auto">
              <CardContent className="p-5 text-rose-200 text-sm">
                Could not load momentum: {error}
              </CardContent>
            </Card>
          )}

          {data && !loading && data.first_run && (
            <Card className="bg-emerald-500/5 border-emerald-500/20 max-w-2xl mx-auto" data-testid="momentum-first-run">
              <CardContent className="p-6 text-center">
                <Calendar className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <h3 className="font-bold text-white mb-1">Capturing first snapshot</h3>
                <p className="text-sm text-slate-400">
                  Weekly momentum will appear here once we have at least 24h of build history.
                </p>
              </CardContent>
            </Card>
          )}

          {data && !loading && !data.first_run && data.delta && (
            <div className="relative lg:flex lg:gap-8">
              <div className="flex-1 min-w-0">
              {/* Recent-milestone callout (auto-expires backend-side) */}
              {data.recent_milestone && (
                <section id="milestone" className="scroll-mt-24">
                  <MilestoneBanner milestone={data.recent_milestone} />
                </section>
              )}

              {/* Public social-proof tile — Iter68b. Only renders if real share traffic exists. */}
              <SocialProofTile shareStats={shareStats} />

              {/* Public milestones timeline — Iter72b. Stepper of crossed gates with ETA for the next one. */}
              <MilestonesTimeline
                milestones={milestones}
                stats={data.current}
                delta={data.delta}
                daysCompared={data.days_compared}
              />

              {/* Public newsletter signup — Iter72c. Hidden when no follower-traffic yet. */}
              <FollowMomentumTile
                milestones={milestones}
                stats={data.current}
                delta={data.delta}
                daysCompared={data.days_compared}
              />

              {/* Goal-hit celebration banner — auto-shows when monolith ≤ target */}
              {timeline?.target && timeline.days?.length > 0 && (
                <GoalCelebrationBanner
                  current={timeline.days[timeline.days.length - 1].monolith_lines}
                  target={timeline.target}
                  stats={data.current}
                  delta={data.delta}
                  daysCompared={data.days_compared}
                />
              )}

              {/* Hero delta card */}
              <section id="deltas" className="scroll-mt-24">
              <Card className="bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 border-emerald-500/30 mb-6" data-testid="momentum-hero-card">
                <CardContent className="p-7">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-cyan-400" />
                    <span className="text-xs uppercase tracking-widest text-cyan-300 font-bold">
                      LAST {data.days_compared}-DAY DELTA
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <DeltaStat
                      raw={data.delta.monolith_lines}
                      label="Lines removed from monolith"
                      goodIfNegative
                    />
                    <DeltaStat
                      raw={data.delta.extracted_from_monolith}
                      label="New modules extracted"
                    />
                    <DeltaStat
                      raw={data.delta.endpoints_remaining}
                      label="Endpoints moved out of monolith"
                      goodIfNegative
                    />
                    <DeltaStat
                      raw={data.delta.router_files}
                      label="New router files"
                    />
                  </div>
                </CardContent>
              </Card>
              </section>

              {/* 30-day monolith-shrink sparkline */}
              <section id="trajectory" className="scroll-mt-24">
              {timeline && timeline.count >= 2 && (
                <SparklineCard timeline={timeline} />
              )}
              </section>

              {/* Current state stats */}
              <section id="state" className="scroll-mt-24">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <CurrentStatCard
                  label="Monolith size today"
                  value={data.current.monolith_lines.toLocaleString()}
                  unit="lines"
                />
                <CurrentStatCard
                  label="Modules extracted"
                  value={data.current.extracted_from_monolith}
                  unit={`of ${data.current.router_files} total`}
                />
                <CurrentStatCard
                  label="Live endpoints"
                  value={data.current.total_endpoints}
                  unit={`across ${data.current.router_files} files`}
                />
              </div>
              </section>

              {/* Engineering principles strip */}
              <section id="principles" className="scroll-mt-24">
              <Card className="bg-white/[0.03] border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <GitBranch className="w-4 h-4 text-cyan-400" />
                    <span className="font-bold text-white">How we ship</span>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-400">
                    <li>Every extracted module ships with a contract test that hits its endpoints via the same URL the frontend uses.</li>
                    <li>Every authenticated endpoint routes through a per-org physical collection — multi-tenant isolation is structural, not procedural.</li>
                    <li>Pro features are gated by a single dependency. Free / expired-trial users get a structured 402 — never a stack trace.</li>
                    <li>Daily snapshot of build state in the database — that's where the deltas above come from. No external analytics.</li>
                  </ul>
                </CardContent>
              </Card>
              </section>

              {/* Recent batches — ANONYMISED (Iter84d). Used to show
                  routes/{module}.py names + per-module endpoint lists,
                  which was handing competitors our entire feature tree.
                  Now shows generic "Batch N · X endpoints · Y lines" rows
                  with no link-through to per-module detail. */}
              <section id="batches" className="scroll-mt-24">
              {recent && recent.extractions && recent.extractions.length > 0 && (
                <Card className="bg-white/[0.03] border-white/10 mt-6" data-testid="recent-batches-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <FileCode2 className="w-4 h-4 text-emerald-400" />
                      <span className="font-bold text-white">Recent extraction batches</span>
                      <Badge className="ml-auto bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[11px] tracking-widest">
                        LAST {recent.extractions.length}
                      </Badge>
                    </div>
                    <ul className="divide-y divide-white/5">
                      {recent.extractions.map((batch, idx) => (
                        <li
                          key={`${batch.iter}-${idx}`}
                          data-testid={`batch-${batch.iter}`}
                          className="py-2.5 flex items-center gap-3 text-sm"
                        >
                          <Badge className="bg-emerald-500/10 text-cyan-300 border-emerald-500/30 text-[11px] font-bold tracking-widest min-w-[58px] justify-center">
                            {batch.iter.toUpperCase()}
                          </Badge>
                          <span className="text-slate-300 font-mono text-[13px]">
                            Batch {batch.iter.replace(/[^0-9]/g, '') || idx + 1}
                          </span>
                          <span className="ml-auto flex items-center gap-3 text-slate-400 text-xs tabular-nums">
                            <span>{batch.endpoint_count} endpoints</span>
                            <span className="text-slate-400">·</span>
                            <span>{batch.line_count.toLocaleString()} lines</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Public API discovery tile — Iter76c */}
              <Link
                to="/build/api"
                className="block mt-6 group"
                data-testid="momentum-api-link"
              >
                <Card className="bg-gradient-to-br from-cyan-500/[0.06] via-emerald-500/[0.04] to-slate-900/20 border-emerald-400/20 hover:border-emerald-400/50 transition-all">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-cyan-300"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-bold text-white text-sm">Public milestone API</span>
                        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[11px] tracking-widest">
                          NEW
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-400">
                        Four formats · no auth · byte-for-byte parity across channels
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-cyan-300 group-hover:translate-x-1 transition-all shrink-0" />
                  </CardContent>
                </Card>
              </Link>
              </section>
              </div>

              {/* Sticky right-side mini-nav (lg+ only) */}
              <SectionMiniNav
                hasMilestone={!!data.recent_milestone}
                milestoneDaysLeft={data.recent_milestone?.days_remaining}
                deltaLines={data.delta?.monolith_lines}
                trajectoryDelta={timeline?.days?.length >= 2
                  ? timeline.days[timeline.days.length - 1].monolith_lines - timeline.days[0].monolith_lines
                  : null}
                totalEndpoints={data.current?.total_endpoints}
                batchCount={recent?.extractions?.length}
              />
            </div>
          )}

          <div className="text-center mt-10">
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => window.location.href = '/pricing'}
                size="lg"
                className="bg-cyan-500 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white px-8 h-12"
                data-testid="momentum-cta-btn"
              >
                See pricing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <ShareButton />
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Updates daily · Public · No tracking cookies
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const SECTION_DEFS = [
  { id: 'milestone', label: 'Milestone', requireMilestone: true, counterKey: 'milestoneDaysLeft' },
  { id: 'deltas', label: 'Deltas', counterKey: 'deltaLines' },
  { id: 'trajectory', label: 'Trajectory', counterKey: 'trajectoryDelta' },
  { id: 'state', label: 'State', counterKey: 'totalEndpoints' },
  { id: 'principles', label: 'Principles' },
  { id: 'batches', label: 'Batches', counterKey: 'batchCount' },
];

/** Format a counter value for display next to a section label. */
const formatCounter = (key, val) => {
  if (val == null) return null;
  if (key === 'milestoneDaysLeft') return { text: `${val}d`, tone: 'neutral' };
  if (key === 'deltaLines') {
    return {
      text: val > 0 ? `+${val}` : `${val}`,
      tone: val < 0 ? 'good' : val > 0 ? 'bad' : 'neutral',
    };
  }
  if (key === 'trajectoryDelta') {
    if (Math.abs(val) >= 1000) {
      return {
        text: `${val < 0 ? '-' : '+'}${(Math.abs(val) / 1000).toFixed(1)}k`,
        tone: val < 0 ? 'good' : val > 0 ? 'bad' : 'neutral',
      };
    }
    return {
      text: `${val > 0 ? '+' : ''}${val}`,
      tone: val < 0 ? 'good' : val > 0 ? 'bad' : 'neutral',
    };
  }
  if (key === 'totalEndpoints' || key === 'batchCount') {
    return { text: `${val}`, tone: 'neutral' };
  }
  return { text: `${val}`, tone: 'neutral' };
};

const SectionMiniNav = ({ hasMilestone, ...counters }) => {
  const sections = SECTION_DEFS.filter((s) => !s.requireMilestone || hasMilestone);
  const [activeId, setActiveId] = useState(sections[0]?.id || null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toneClass = {
    good: 'text-emerald-400/80',
    bad: 'text-rose-400/80',
    neutral: 'text-slate-400',
  };

  return (
    <nav
      className="hidden lg:block w-44 shrink-0"
      aria-label="Page sections"
      data-testid="momentum-mini-nav"
    >
      <div className="sticky top-24">
        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3 pl-3">
          On this page
        </div>
        <ul className="space-y-0.5 border-l border-white/5">
          {sections.map((s) => {
            const isActive = activeId === s.id;
            const counter = s.counterKey
              ? formatCounter(s.counterKey, counters[s.counterKey])
              : null;
            return (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  onClick={(e) => handleClick(e, s.id)}
                  data-testid={`mini-nav-${s.id}`}
                  className={`relative flex items-center justify-between gap-2 py-1.5 pl-3 pr-2 text-xs transition-all
                    ${isActive
                      ? 'text-cyan-300 font-semibold'
                      : 'text-slate-400 hover:text-slate-300'}`}
                >
                  {isActive && (
                    <span className="absolute left-0 top-0 bottom-0 w-px bg-cyan-400" />
                  )}
                  <span>{s.label}</span>
                  {counter && (
                    <span
                      data-testid={`mini-nav-counter-${s.id}`}
                      className={`text-[11px] font-mono tabular-nums ${
                        isActive ? 'text-cyan-300/80' : toneClass[counter.tone]
                      }`}
                    >
                      {counter.text}
                    </span>
                  )}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

const MilestoneBanner = ({ milestone }) => {
  if (!milestone) return null;
  return (
    <div
      className="relative mb-6 overflow-hidden rounded-xl border border-emerald-400/40 bg-gradient-to-br from-cyan-500/10 via-emerald-500/5 to-teal-500/10 p-6"
      data-testid="momentum-milestone-banner"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="rounded-lg bg-emerald-500/20 border border-emerald-400/30 p-2">
          <Flag className="w-5 h-5 text-cyan-300" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[11px] uppercase tracking-widest font-bold text-cyan-300">
              Recent milestone
            </span>
            <span className="text-[11px] text-slate-400 tabular-nums">
              · expires in {milestone.days_remaining} day
              {milestone.days_remaining === 1 ? '' : 's'}
            </span>
          </div>
          <div className="text-base font-bold text-white mb-1.5">
            {milestone.title}
          </div>
          <div className="text-xs text-slate-300 leading-relaxed">
            {milestone.subtitle}
          </div>
        </div>
      </div>
      {milestone.stats && milestone.stats.length > 0 && (
        <div
          className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3"
          data-testid="momentum-milestone-stats"
        >
          {milestone.stats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-lg bg-slate-900/40 border border-white/5 px-3 py-2"
            >
              <div className="text-base font-bold text-white tabular-nums">
                {stat.value}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-slate-400">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GoalCelebrationBanner = ({ current, target, stats, delta, daysCompared }) => {
  if (!target || current > target.monolith_lines) return null;
  const linesUnder = target.monolith_lines - current;
  // Suggest the next milestone — the next 5% increment past the hit goal.
  const nextPct = target.reduction_pct + 5;

  // Build a personalised LinkedIn post with live momentum stats.
  const publicUrl = typeof window !== 'undefined' ? window.location.origin + '/build/momentum' : '';
  // UTM-tagged URL so we can attribute LinkedIn-driven visits in admin analytics.
  const utmUrl = `${publicUrl}?utm_source=linkedin&v=${current}`;
  // starting_lines is not on /build/momentum; derive it from the known goal
  // (target.monolith_lines at target.reduction_pct% reduction).
  const startingLines = target.reduction_pct > 0
    ? Math.round(target.monolith_lines / (1 - target.reduction_pct / 100))
    : null;
  const reductionPct = startingLines
    ? (((startingLines - current) / startingLines) * 100).toFixed(2)
    : target.reduction_pct;
  const linesReduced = startingLines
    ? (startingLines - current).toLocaleString()
    : '';
  const modules = stats?.extracted_from_monolith;

  // Pick the most-impactful single stat from the last N-day delta.
  // The delta object signs are inverted for reduction/removal metrics —
  // a NEGATIVE monolith_lines delta means we removed lines (good).
  const headlineDelta = (() => {
    if (!delta) return null;
    const dayWord = `${daysCompared || 7} days`;
    const linesRemoved = -1 * (delta.monolith_lines || 0);
    const endpointsMoved = -1 * (delta.endpoints_remaining || 0);
    const newModules = delta.extracted_from_monolith || 0;
    if (linesRemoved >= 100) {
      return `${linesRemoved.toLocaleString()} lines removed in ${dayWord}`;
    }
    if (newModules >= 1) {
      return `${newModules} module${newModules === 1 ? '' : 's'} extracted in ${dayWord}`;
    }
    if (endpointsMoved >= 1) {
      return `${endpointsMoved} endpoint${endpointsMoved === 1 ? '' : 's'} moved out of the monolith in ${dayWord}`;
    }
    return null;
  })();

  // Iter73b — role-aware share kits. Each persona gets a different
  // intro line so the post resonates with the actual reader instead of
  // looking generic. Backend stores `role` on the click event for
  // attribution; defaults to no-role when the user picks "Default".
  const roleIntros = {
    default: '',
    engineer: 'As an engineer, what I love about CARTIEAI: ',
    cto: 'As a CTO evaluating FinOps tooling, what struck me about CARTIEAI: ',
    vc: 'A founder team that ships in the open is a strong signal — CARTIEAI just hit a milestone: ',
    founder: 'Fellow founders — if you ever wondered what shipping in the open looks like, this is it. CARTIEAI: ',
  };

  // Rotate the headline copy on every render — visitors get a slightly
  // different framing each time, which keeps the share card feeling alive.
  const variants = [
    headlineDelta
      ? `This week on CARTIEAI: ${headlineDelta}. Monolith now ${reductionPct}% smaller than where we started.`
      : null,
    `Just crossed ${reductionPct}% monolith reduction on CARTIEAI — our FinOps OS for Cloud.`,
    headlineDelta
      ? `${headlineDelta} on CARTIEAI. Total monolith reduction now sits at ${reductionPct}%.`
      : null,
    `${reductionPct}% of the monolith — gone. ${modules || 0} modules extracted, zero regressions, all in the open.`,
  ].filter(Boolean);
  // Hash by current line-count so a given snapshot always picks the same
  // variant within a single visit (looks intentional, not random).
  const headline = variants[current % variants.length];

  const buildPost = (role) => {
    const intro = roleIntros[role] || '';
    return `${intro}${headline}

${linesReduced ? `· ${linesReduced} lines removed from the monolith\n` : ''}${modules ? `· ${modules} modules extracted · zero regressions\n` : ''}· Built in the open, refactored daily

Live momentum dashboard → ${utmUrl}`;
  };
  const linkedinHref = (post) =>
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(utmUrl)}` +
    `&summary=${encodeURIComponent(post)}`;

  const onLinkedinClick = async (role, post) => {
    // Fire-and-forget: copy the post to clipboard AND track the click.
    try { await navigator.clipboard.writeText(post); } catch { /* noop */ }
    try {
      await axios.post(`${API}/build/share-click`, {
        source: 'linkedin',
        snapshot: current,
        role: role && role !== 'default' ? role : null,
      });
    } catch { /* noop */ }
  };

  return (
    <div
      className="relative mb-6 overflow-hidden rounded-xl border border-amber-400/40 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-rose-500/10 p-5"
      data-testid="momentum-goal-celebration"
    >
      {/* Confetti — pure CSS, 12 floating dots */}
      <div className="pointer-events-none absolute inset-0 opacity-60">
        {Array.from({ length: 12 }).map((_, i) => (
          <span
            key={i}
            className="absolute block w-1 h-1 rounded-full"
            style={{
              left: `${(i * 8.3 + 4) % 100}%`,
              top: `${(i * 13) % 80 + 10}%`,
              background: ['#fbbf24', '#f97316', '#22d3ee', '#34d399', '#22d3ee'][i % 5],
              animation: `confetti-float ${2.4 + (i % 3) * 0.6}s ease-in-out ${i * 0.12}s infinite`,
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes confetti-float {
          0%   { transform: translateY(0)    rotate(0deg);   opacity: 0.6; }
          50%  { transform: translateY(-8px) rotate(180deg); opacity: 1;   }
          100% { transform: translateY(0)    rotate(360deg); opacity: 0.6; }
        }
      `}</style>
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-3 flex-1">
          <PartyPopper className="w-6 h-6 text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-bold text-amber-100 mb-0.5">
              We hit our {target.label}.
            </div>
            <div className="text-xs text-amber-200/80">
              Monolith is {linesUnder.toLocaleString()} {linesUnder === 1 ? 'line' : 'lines'} under the {target.label} target ({target.monolith_lines.toLocaleString()} lines).
              Now aiming for {nextPct}% reduction.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RoleAwareLinkedinShare
            buildPost={buildPost}
            linkedinHref={linkedinHref}
            onLinkedinClick={onLinkedinClick}
          />
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/og/share-card.svg`}
            download="cartieai-30-percent-crossed.svg"
            data-testid="momentum-celebration-share-card"
            className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/20 text-[11px] font-bold text-amber-100 hover:text-white transition-all"
          >
            <ArrowRight className="w-3 h-3" />
            OG card
          </a>
        </div>
      </div>
    </div>
  );
};

const ShareButton = () => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/og/build-momentum`;

  const handleCopy = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'CARTIEAI — Live Build Momentum',
          text: 'Built in the open. Refactored daily.',
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // user cancelled share or clipboard failed — silent
    }
  };

  return (
    <Button
      onClick={handleCopy}
      size="lg"
      variant="outline"
      className="border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 text-cyan-200 hover:text-cyan-100 px-6 h-12"
      data-testid="momentum-share-btn"
    >
      {copied ? (
        <>
          <Check className="w-4 h-4 mr-2" />
          Link copied
        </>
      ) : (
        <>
          <Share2 className="w-4 h-4 mr-2" />
          Share with rich preview
        </>
      )}
    </Button>
  );
};

const DeltaStat = ({ raw, label, goodIfNegative = false }) => {
  const positive = goodIfNegative ? raw < 0 : raw > 0;
  const negative = goodIfNegative ? raw > 0 : raw < 0;
  const tone = positive ? 'text-emerald-400' : negative ? 'text-rose-400' : 'text-slate-400';
  const Icon = positive ? TrendingDown : negative ? TrendingUp : null;
  const formatted = raw > 0 ? `+${raw.toLocaleString()}` : raw.toLocaleString();
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <span className={`text-3xl font-bold tabular-nums ${tone}`}>{formatted}</span>
        {Icon && <Icon className={`w-5 h-5 ${tone}`} />}
      </div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  );
};

const CurrentStatCard = ({ label, value, unit }) => (
  <Card className="bg-slate-900/60 border-white/10">
    <CardContent className="p-5">
      <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-white">{value}</span>
        <span className="text-xs text-slate-400">{unit}</span>
      </div>
    </CardContent>
  </Card>
);

const SparklineCard = ({ timeline }) => {
  const series = timeline.days || [];
  if (series.length < 2) return null;

  const target = timeline.target;
  const linesArr = series.map((d) => d.monolith_lines);
  const extractedArr = series.map((d) => d.extracted_from_monolith);

  // Y-axis: include target so the dashed goal line is always visible
  // even when the current data hasn't reached it yet.
  const linesMaxRaw = Math.max(...linesArr);
  const linesMinRaw = Math.min(...linesArr);
  const linesMax = target ? Math.max(linesMaxRaw, target.monolith_lines) : linesMaxRaw;
  const linesMin = target ? Math.min(linesMinRaw, target.monolith_lines) : linesMinRaw;
  const linesRange = Math.max(1, linesMax - linesMin);

  const extMax = Math.max(...extractedArr);
  const extMin = Math.min(...extractedArr);
  const extRange = Math.max(1, extMax - extMin);

  const W = 600;
  const H = 80;
  const stepX = W / (series.length - 1);

  // Path 1: monolith lines (descending, with area fill)
  const linePoints = series.map((d, i) => [
    i * stepX,
    H - ((d.monolith_lines - linesMin) / linesRange) * H,
  ]);
  const linePath = linePoints
    .map(([x, y], i) => (i === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` : `L${x.toFixed(1)},${y.toFixed(1)}`))
    .join(' ');
  const areaPath = `${linePath} L${W.toFixed(1)},${H} L0,${H} Z`;

  // Path 2: modules extracted (ascending, stroke-only)
  const extPoints = series.map((d, i) => [
    i * stepX,
    H - ((d.extracted_from_monolith - extMin) / extRange) * H,
  ]);
  const extPath = extPoints
    .map(([x, y], i) => (i === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` : `L${x.toFixed(1)},${y.toFixed(1)}`))
    .join(' ');

  // Target line y-coordinate
  const targetY = target
    ? H - ((target.monolith_lines - linesMin) / linesRange) * H
    : null;

  const start = series[0];
  const end = series[series.length - 1];
  const totalDelta = end.monolith_lines - start.monolith_lines;
  const totalExtractedDelta = end.extracted_from_monolith - start.extracted_from_monolith;
  const startDate = new Date(start.date);
  const endDate = new Date(end.date);
  const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // How far we still are from the target
  const linesToGoal = target ? end.monolith_lines - target.monolith_lines : null;

  return (
    <Card className="bg-slate-900/60 border-white/10 mb-6" data-testid="momentum-sparkline-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">
            {series.length}-day monolith trajectory
          </span>
          <span className="text-xs text-slate-400">
            {fmtDate(startDate)} → {fmtDate(endDate)}
          </span>
        </div>
        <div className="flex items-baseline gap-3 mb-3 flex-wrap">
          <span className="text-2xl font-bold text-white tabular-nums">
            {end.monolith_lines.toLocaleString()}
          </span>
          <span className="text-xs text-slate-400">lines today</span>
          <span
            className={`text-sm font-bold tabular-nums ${
              totalDelta < 0 ? 'text-emerald-400' : totalDelta > 0 ? 'text-rose-400' : 'text-slate-400'
            }`}
          >
            {totalDelta > 0 ? '+' : ''}
            {totalDelta.toLocaleString()}
          </span>
          {totalExtractedDelta > 0 && (
            <span className="text-xs text-emerald-300">
              · +{totalExtractedDelta} module{totalExtractedDelta > 1 ? 's' : ''} extracted
            </span>
          )}
          {target && linesToGoal !== null && linesToGoal > 0 && (
            <span className="text-xs text-amber-300" data-testid="momentum-goal-distance">
              · {linesToGoal.toLocaleString()} lines from {target.label}
            </span>
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mb-2 text-[11px] uppercase tracking-widest flex-wrap">
          <span className="flex items-center gap-1.5 text-cyan-300">
            <span className="inline-block w-3 h-0.5 bg-cyan-400" />
            Monolith size
          </span>
          <span className="flex items-center gap-1.5 text-emerald-300">
            <span className="inline-block w-3 h-0.5 bg-emerald-400" style={{ borderTop: '1px dashed' }} />
            Modules extracted
          </span>
          {target && (
            <span className="flex items-center gap-1.5 text-amber-300" data-testid="momentum-goal-legend">
              <span className="inline-block w-3 h-px border-t border-dashed border-amber-400" />
              {target.label} ({target.deadline})
            </span>
          )}
        </div>

        <div className="relative">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full h-20"
            preserveAspectRatio="none"
            aria-label="Monolith size trend, modules-extracted trend, and modularization goal"
          >
            <defs>
              <linearGradient id="sparkAreaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {/* Cyan area + line — monolith descent */}
            <path d={areaPath} fill="url(#sparkAreaFill)" />
            <path d={linePath} fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            {/* Emerald dashed line — extraction ascent */}
            <path
              d={extPath}
              fill="none"
              stroke="#34d399"
              strokeWidth="1.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              strokeDasharray="4 3"
              opacity="0.85"
            />
            {/* Amber dashed line — modularization target */}
            {target && targetY !== null && (
              <>
                <line
                  x1="0"
                  y1={targetY}
                  x2={W}
                  y2={targetY}
                  stroke="#fbbf24"
                  strokeWidth="1"
                  strokeDasharray="3 4"
                  opacity="0.6"
                />
                <text
                  x={W - 6}
                  y={targetY - 4}
                  fill="#fbbf24"
                  fontSize="9"
                  fontWeight="bold"
                  textAnchor="end"
                  opacity="0.85"
                >
                  {target.label.toUpperCase()}
                </text>
              </>
            )}
            {/* End-of-line dots */}
            {linePoints.length > 0 && (
              <circle
                cx={linePoints[linePoints.length - 1][0]}
                cy={linePoints[linePoints.length - 1][1]}
                r="3"
                fill="#22d3ee"
                stroke="#0f172a"
                strokeWidth="1.5"
              />
            )}
            {extPoints.length > 0 && (
              <circle
                cx={extPoints[extPoints.length - 1][0]}
                cy={extPoints[extPoints.length - 1][1]}
                r="2.5"
                fill="#34d399"
                stroke="#0f172a"
                strokeWidth="1.5"
              />
            )}
          </svg>
          <div className="flex justify-between text-[11px] text-slate-400 mt-1 tabular-nums">
            <span>{linesMaxRaw.toLocaleString()}</span>
            <span>{linesMinRaw.toLocaleString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Social-proof tile ──────────────────────────────────────────────────────
// Renders a compact "X LinkedIn shares · Y visits this week" line under
// the milestone banner. Hidden until real, public share traffic exists so
// it never shows a misleading "0 shares" empty state.
//
// Privacy note (Iter84ae): consumes the BUCKETED public shape — popularity
// label + channel list, never raw counts. The previous shape (totals.clicks
// + per-source counts) was a funnel-intelligence leak and has been removed
// from the public endpoint.
const SocialProofTile = ({ shareStats }) => {
  if (!shareStats) return null;
  const { popularity_bucket: bucket, channels = [], days } = shareStats;
  if (!bucket || bucket === 'Quiet week' || channels.length === 0) {
    return null;  // hide on quiet weeks / when nothing's been shared yet
  }

  const headlineLabel = channels[0].charAt(0).toUpperCase() + channels[0].slice(1);
  const channelSummary = channels.length === 1
    ? headlineLabel
    : `${headlineLabel} +${channels.length - 1} more`;

  return (
    <div
      className="mb-6 flex flex-wrap items-center gap-2 px-4 py-3 rounded-xl border border-sky-500/20 bg-emerald-500/5"
      data-testid="momentum-social-proof"
    >
      <Linkedin className="w-4 h-4 text-sky-300" />
      <span className="text-sm text-sky-100">
        <span className="font-bold text-white" data-testid="momentum-social-proof-bucket">
          {bucket}
        </span>
        <span className="text-slate-400"> · </span>
        <span className="text-sky-200" data-testid="momentum-social-proof-channels">
          {channelSummary}
        </span>
        <span className="text-slate-400 text-xs ml-1">in the last {days} days</span>
      </span>
      <span className="ml-auto text-[11px] uppercase tracking-widest text-sky-400/80 font-bold">
        Live · Built in the open
      </span>
    </div>
  );
};

// ─── Milestones Timeline Stepper ────────────────────────────────────────────
// Iter72b — public horizontal stepper of every 5%-reduction gate.
// Crossed gates show ✓ + the date they were crossed. The next gate shows
// an ETA based on the current 7-day reduction velocity.
const MILESTONE_GATES = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75];

const MilestonesTimeline = ({ milestones, stats, delta, daysCompared }) => {
  if (!milestones || !stats) return null;
  const crossings = milestones.milestones || [];
  if (crossings.length === 0) return null; // nothing to show yet

  const crossedMap = new Map();
  for (const c of crossings) crossedMap.set(c.milestone_pct, c);

  // Current reduction %.
  const startingLines = stats.starting_lines || 7940;
  const current = stats.monolith_lines || startingLines;
  const reductionPct = ((startingLines - current) / startingLines) * 100;

  // Find the next un-crossed gate.
  const nextGate = MILESTONE_GATES.find(g => !crossedMap.has(g) && g > reductionPct);

  // ETA: lines/day from delta (negative monolith_lines = removed).
  const linesPerDay = delta && daysCompared
    ? Math.max(0, (-1 * (delta.monolith_lines || 0)) / daysCompared)
    : 0;
  const linesToNextGate = nextGate
    ? Math.max(0, current - Math.round(startingLines * (1 - nextGate / 100)))
    : 0;
  const etaDays = nextGate && linesPerDay > 0
    ? Math.ceil(linesToNextGate / linesPerDay)
    : null;

  const visibleGates = MILESTONE_GATES.filter(g =>
    crossedMap.has(g) || g === nextGate || g <= reductionPct + 5
  );

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Card
      className="mb-6 bg-gradient-to-br from-amber-500/5 via-slate-900/60 to-cyan-500/5 border-amber-500/20"
      data-testid="momentum-milestones-timeline"
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-4 h-4 text-amber-300" />
          <span className="text-[11px] uppercase tracking-widest text-amber-300 font-bold">
            MILESTONES TIMELINE
          </span>
          {nextGate && etaDays !== null && (
            <span className="ml-auto text-xs text-slate-400">
              Next: <span className="text-cyan-300 font-bold">{nextGate}%</span>
              <span className="text-slate-400"> in</span>
              <span className="text-white font-bold ml-1">~{etaDays}d</span>
            </span>
          )}
          {nextGate && etaDays === null && (
            <span className="ml-auto text-xs text-slate-400">
              Next gate: <span className="text-cyan-300 font-bold">{nextGate}%</span>
            </span>
          )}
        </div>
        <div className="relative pt-2 pb-1">
          {/* Track */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-800 -translate-y-1/2" />
          {/* Filled portion up to current reduction */}
          <div
            className="absolute top-1/2 left-0 h-0.5 bg-amber-400 bg-gradient-to-r from-amber-400 via-cyan-400 to-emerald-400 -translate-y-1/2 transition-all duration-700"
            style={{
              width: `${Math.min(100, (reductionPct / 75) * 100)}%`,
            }}
          />
          {/* Gate dots */}
          <div className="relative flex justify-between items-center">
            {visibleGates.map((g) => {
              const crossed = crossedMap.has(g);
              const isNext = g === nextGate;
              const doc = crossedMap.get(g);
              return (
                <div
                  key={g}
                  className="flex flex-col items-center gap-1.5 group"
                  data-testid={`milestone-gate-${g}`}
                  data-crossed={crossed ? 'true' : 'false'}
                >
                  <div
                    className={
                      crossed
                        ? 'relative w-6 h-6 rounded-full bg-amber-400 border-2 border-amber-500/30 flex items-center justify-center shadow-[0_0_12px_rgba(251,191,36,0.45)]'
                        : isNext
                          ? 'relative w-6 h-6 rounded-full bg-slate-900 border-2 border-emerald-400 animate-pulse flex items-center justify-center'
                          : 'relative w-6 h-6 rounded-full bg-slate-900 border-2 border-slate-700'
                    }
                  >
                    {crossed && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                    {!crossed && isNext && (
                      <Flag className="w-3 h-3 text-cyan-300" />
                    )}
                  </div>
                  <div className="flex flex-col items-center">
                    <span
                      className={
                        crossed
                          ? 'text-[11px] font-bold text-amber-200'
                          : isNext
                            ? 'text-[11px] font-bold text-cyan-200'
                            : 'text-[11px] font-bold text-slate-400'
                      }
                    >
                      {g}%
                    </span>
                    {crossed && doc && (
                      <span className="text-[11px] text-amber-300/70 mt-0.5 hidden sm:block">
                        {formatDate(doc.announced_at)}
                      </span>
                    )}
                  </div>
                  {/* Hover tooltip — share buttons for crossed gates */}
                  {crossed && doc && (
                    <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 invisible group-hover:visible bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-xl z-10 whitespace-nowrap">
                      <div className="text-[11px] text-slate-400 mb-1.5">
                        Crossed {formatDate(doc.announced_at)}
                      </div>
                      <div className="flex gap-1.5">
                        <a
                          href={doc.linkedin_share_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#0a66c2]/20 hover:bg-[#0a66c2]/40 text-sky-200 text-[11px] font-semibold transition-colors"
                          data-testid={`milestone-share-li-${g}`}
                        >
                          <Linkedin className="w-2.5 h-2.5" /> Share
                        </a>
                        <a
                          href={doc.twitter_share_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-semibold transition-colors"
                          data-testid={`milestone-share-x-${g}`}
                        >
                          <Twitter className="w-2.5 h-2.5" /> Post
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Follow Momentum Tile ───────────────────────────────────────────────────
// Iter72c — lightweight email signup. When a visitor submits, we:
//   1) POST /api/build/follow with their email + source 'build-momentum'
//   2) Show a success state with the next gate + ETA
//   3) Render an active follower count above the form using
//      /api/build/followers-count.
const FollowMomentumTile = ({ milestones, stats, delta, daysCompared }) => {
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState('idle'); // idle | loading | success | already | error
  const [count, setCount] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/build/followers-count`)
      .then(r => { if (!cancelled) setCount(r.data?.active_followers ?? 0); })
      .catch(() => { if (!cancelled) setCount(0); });
    return () => { cancelled = true; };
  }, []);

  // Compute next gate + ETA (same logic as MilestonesTimeline).
  const startingLines = stats?.starting_lines || 7940;
  const current = stats?.monolith_lines || startingLines;
  const reductionPct = ((startingLines - current) / startingLines) * 100;
  const crossedSet = new Set(
    (milestones?.milestones || []).map(m => m.milestone_pct)
  );
  const nextGate = MILESTONE_GATES.find(g => !crossedSet.has(g) && g > reductionPct);
  const linesPerDay = delta && daysCompared
    ? Math.max(0, (-1 * (delta.monolith_lines || 0)) / daysCompared)
    : 0;
  const linesToNext = nextGate
    ? Math.max(0, current - Math.round(startingLines * (1 - nextGate / 100)))
    : 0;
  const etaDays = nextGate && linesPerDay > 0
    ? Math.ceil(linesToNext / linesPerDay)
    : null;

  const headlineCount = count !== null && count > 0 ? count : null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setStatus('loading');
    try {
      const r = await axios.post(`${API}/build/follow`, {
        email: email.trim().toLowerCase(),
        source: 'build-momentum',
        snapshot: current,
      });
      if (r.data?.already_subscribed) {
        setStatus('already');
      } else {
        setStatus('success');
        setCount(c => (c === null ? 1 : c + 1));
      }
    } catch (err) {
      setStatus(err.response?.status === 422 ? 'error' : 'error');
    }
  };

  if (status === 'success' || status === 'already') {
    return (
      <Card
        className="mb-6 bg-gradient-to-br from-emerald-500/10 via-slate-900/60 to-cyan-500/10 border-emerald-500/30"
        data-testid="momentum-follow-success"
      >
        <CardContent className="p-5 flex flex-wrap items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
            <Check className="w-5 h-5 text-emerald-300" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white">
              {status === 'already' ? "You're already on the list." : "You're in."}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              {nextGate && etaDays !== null
                ? <>We'll email you when we cross <span className="text-cyan-300 font-semibold">{nextGate}%</span> · expected in <span className="text-white font-bold">~{etaDays}d</span>.</>
                : nextGate
                  ? <>We'll email you when we cross <span className="text-cyan-300 font-semibold">{nextGate}%</span>.</>
                  : <>We'll email you on the next milestone crossing.</>
              }
            </div>
          </div>
          <span className="ml-auto text-[11px] uppercase tracking-widest text-emerald-300/80 font-bold">
            Subscribed
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="mb-6 bg-gradient-to-br from-cyan-500/5 via-slate-900/60 to-emerald-500/5 border-emerald-500/20"
      data-testid="momentum-follow-tile"
    >
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
              <Bell className="w-4 h-4 text-cyan-300" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-white truncate">
                {headlineCount
                  ? <>📈 Join {headlineCount.toLocaleString()} builders watching us hit {nextGate || 'the next gate'}%</>
                  : <>📈 Watch us hit {nextGate || 'the next gate'}%</>
                }
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                One email per milestone. No spam. Unsubscribe anytime.
              </div>
            </div>
          </div>
          <form
            onSubmit={onSubmit}
            className="flex flex-1 min-w-[280px] gap-2 items-center"
            data-testid="momentum-follow-form"
          >
            <div className="flex-1 relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'loading'}
                data-testid="momentum-follow-email-input"
                className="w-full bg-slate-950/60 border border-slate-700 focus:border-emerald-400/60 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-slate-400 outline-none transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={status === 'loading' || !email.includes('@')}
              data-testid="momentum-follow-submit-button"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-cyan-400 disabled:bg-slate-700 disabled:cursor-not-allowed text-slate-950 disabled:text-slate-400 text-sm font-bold transition-colors"
            >
              {status === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              Notify me
            </button>
          </form>
        </div>
        {status === 'error' && (
          <div
            className="mt-3 text-xs text-rose-300"
            data-testid="momentum-follow-error"
          >
            Hmm, that didn't work. Try a different address?
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Role-Aware LinkedIn Share Button ───────────────────────────────────────
// Iter73b — adds a tiny role selector (Engineer / CTO / VC / Founder /
// Default) next to the LinkedIn button. Each role pre-pends a tailored
// intro line so the post resonates with the actual reader.
const RoleAwareLinkedinShare = ({ buildPost, linkedinHref, onLinkedinClick }) => {
  const [role, setRole] = React.useState('default');
  const post = buildPost(role);
  return (
    <div className="flex items-center gap-1.5">
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        data-testid="momentum-share-role-select"
        className="bg-slate-900/80 border border-slate-700 hover:border-slate-600 text-[11px] text-slate-300 rounded-md px-2 py-1.5 outline-none focus:border-emerald-400/60 transition-colors cursor-pointer"
        aria-label="Pick a persona for the LinkedIn post"
      >
        <option value="default">As me</option>
        <option value="engineer">As an engineer</option>
        <option value="cto">As a CTO</option>
        <option value="vc">As a VC</option>
        <option value="founder">As a founder</option>
      </select>
      <a
        href={linkedinHref(post)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onLinkedinClick(role, post)}
        data-testid="momentum-celebration-linkedin-share"
        data-role={role}
        className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#0a66c2]/50 bg-[#0a66c2]/15 hover:bg-[#0a66c2]/30 text-[11px] font-bold text-sky-100 hover:text-white transition-all"
      >
        <Linkedin className="w-3 h-3" />
        Share on LinkedIn
      </a>
    </div>
  );
};


export default BuildMomentumPage;
